import _get from 'lodash/get'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Text, View } from 'react-native'
import type { KeyboardType } from 'react-native/Libraries/Components/TextInput/TextInput'
import { P, match } from 'ts-pattern'

import { useFlowField } from '@flows-ui/components/useFlowField'
import { resolveStepContentElementValue } from '@flows-ui/utils/resolveStepContentElementValue'
import type { TrackFn } from '@flows/configs/analytics/blurTracking'
import type { AnyCallbackConfig } from '@flows/core/types/dispatchedHookTypes'
import type { Input as InputProps } from '@flows/core/types/stepContentTypes'
import type { AnySimpleOnSubmitHook, FlowValues } from '@flows/core/types/stepTypes'
import TextField from '@ui-kit/components/formComponents/TextField'
import { body1 } from '@ui-kit/core/typography'

interface Props<
  TCallbackConfig extends AnyCallbackConfig,
  TSimpleOnSubmitHook extends AnySimpleOnSubmitHook,
> extends InputProps<TCallbackConfig, TSimpleOnSubmitHook> {
  flowValues: FlowValues
  track?: TrackFn
}

export const Input = <TProps extends Props<any, any>>(props: TProps) => {
  const { t } = useTranslation()
  const { field, visibleError, onBlur, translateFunction, label, placeholder } = useFlowField(
    props,
    t
  )
  const configuredValue = resolveStepContentElementValue(props.value, translateFunction)
  const keyboardType = match(props.inputType)
    .returnType<KeyboardType>()
    .with(P.union('integer', 'float'), () => 'number-pad')
    .with('tel', () => 'phone-pad')
    .with('email', () => 'email-address')
    .otherwise(() => 'default')

  const textField = (
    <TextField
      keyboardType={keyboardType}
      disabled={props.disabled}
      testID={props.name}
      autoGrow={props.autoGrow}
      label={props.labelAbove ? undefined : label}
      placeholder={placeholder}
      name={props.name}
      value={field.value ?? configuredValue}
      styleType={props.styleType ?? 'underlined'}
      onValueChange={field.onChange}
      onBlur={onBlur}
      status={visibleError ? 'error' : undefined}
      errorMessage={visibleError}
    />
  )

  return props.labelAbove && label ? (
    <View className="gap-2">
      <Text className={body1({ weight: 'semibold' })}>{label}</Text>
      {textField}
    </View>
  ) : (
    textField
  )
}
